import { useRef } from 'react'

import type { ShopGuideVideo } from '@/lib/api/shop-guide-videos-api'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { getAnalyticsActor, posthog } from '@/lib/posthog'

type GuideVideoEvent = 'guide_video_played' | 'guide_video_completed'

/**
 * Insight docs/analytics/shop-guide-videos.md: chỉ id video (từ API) + actor,
 * không tiêu đề / url. Lỗi analytics không được chặn việc xem.
 */
function track(event: GuideVideoEvent, video: ShopGuideVideo) {
    const identity = getAnalyticsActor()
    if (!identity) return
    try {
        posthog.capture(event, {
            schema_version: 1,
            guide_video_id: video.id,
            ...identity,
        })
    } catch {
        // Analytics failure must not interrupt playback.
    }
}

interface Props {
    video: ShopGuideVideo | null
    onClose: () => void
}

/**
 * Trình phát của màn Video hướng dẫn: thẻ video HTML5 chỉ dựng khi mở (trước
 * đó không tải gì), preload=metadata + tự phát vì người dùng vừa bấm. Video
 * khung điện thoại (dọc) giữ nguyên tỉ lệ nhờ object-contain + giới hạn cao.
 */
export function GuideVideoPlayer({ video, onClose }: Props) {
    return (
        <Dialog open={!!video} onOpenChange={(open) => !open && onClose()}>
            {video && (
                <DialogContent className="max-h-[95svh] gap-3 overflow-y-auto p-3 sm:max-w-4xl sm:p-6">
                    <DialogHeader className="pr-8 text-left">
                        <DialogTitle>{video.title}</DialogTitle>
                        <DialogDescription
                            className={video.description ? '' : 'sr-only'}
                        >
                            {video.description || video.title}
                        </DialogDescription>
                    </DialogHeader>
                    <PlayerVideo key={video.id} video={video} />
                </DialogContent>
            )}
        </Dialog>
    )
}

/** Một lần mở đếm một lần xem / xem hết, dù tua lại hay phát lại. */
function PlayerVideo({ video }: { video: ShopGuideVideo }) {
    const sent = useRef({ played: false, ended: false })
    return (
        <video
            data-testid="guide-video-player"
            src={video.video_url}
            poster={video.poster_url ?? undefined}
            controls
            autoPlay
            playsInline
            preload="metadata"
            className="max-h-[75svh] w-full rounded-md bg-black object-contain"
            onPlay={() => {
                if (sent.current.played) return
                sent.current.played = true
                track('guide_video_played', video)
            }}
            onEnded={() => {
                if (sent.current.ended) return
                sent.current.ended = true
                track('guide_video_completed', video)
            }}
        />
    )
}
